import React from 'react';
import { useNavigate } from 'react-router-dom';
import adminService from '../../services/api/admin';
import { useQuery } from 'react-query';
import { getArabicDate } from '../../services/utils/date';
import {
  Users,
  CreditCard,
  TrendingUp,
  UserCheck,
  UserPlus,
  CheckCircle,
  XCircle,
  AlertCircle,
  Activity,
  DollarSign,
  ShieldCheck,
  ChevronLeft,
  ArrowRight,
  Zap,
  BarChart3,
  Layers,
  Sparkles,
  PieChart,
  Settings,
  BookOpen,
  Clock
} from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const AdminDashboard = () => {
  const navigate = useNavigate();

  // جلب إحصائيات الإدارة العامة (المنطق الأصلي كاملاً)
  const { data: stats, isLoading } = useQuery(
    'admin-stats',
    () => adminService.getStats()
  );

  // جلب سجل الأنشطة الأخيرة
  const { data: activities } = useQuery(
    'admin-activities',
    () => adminService.getActivities({ limit: 10 })
  );

  const navigationItems = [
    {
      title: 'إدارة المستخدمين',
      description: 'التحكم في حسابات الطلاب والمدرسين',
      icon: Users,
      link: '/admin/users',
      color: 'bg-blue-600',
      shadow: 'shadow-blue-100'
    },
    {
      title: 'إدارة المحتوى',
      description: 'مراجعة وتعديل الكورسات والدروس',
      icon: BookOpen,
      link: '/admin/content',
      color: 'bg-emerald-600',
      shadow: 'shadow-emerald-100'
    },
    {
      title: 'طلبات الانضمام',
      description: 'مراجعة واعتماد طلبات المدرسين',
      icon: UserCheck,
      link: '/admin/teacher-approval',
      color: 'bg-violet-600',
      shadow: 'shadow-violet-100'
    },
    {
      title: 'السجل المالي',
      description: 'تتبع الاشتراكات والتدفقات المالية',
      icon: CreditCard,
      link: '/admin/subscriptions',
      color: 'bg-amber-500',
      shadow: 'shadow-amber-100'
    },
    {
      title: 'إعدادات النظام',
      description: 'ضبط إعدادات المنصة والأمان والدفع',
      icon: Settings,
      link: '/admin/settings',
      color: 'bg-indigo-600',
      shadow: 'shadow-indigo-100'
    }
  ];

  const getActivityIcon = (type) => {
    switch (type) {
      case 'user_registered': return <UserPlus className="w-5 h-5 text-emerald-500" />;
      case 'course_created': return <BookOpen className="w-5 h-5 text-blue-500" />;
      case 'subscription_created': return <DollarSign className="w-5 h-5 text-amber-500" />;
      case 'teacher_approved': return <CheckCircle className="w-5 h-5 text-emerald-500" />;
      case 'teacher_rejected': return <XCircle className="w-5 h-5 text-rose-500" />;
      default: return <Zap className="w-5 h-5 text-gray-400" />;
    }
  };

  const getActivityText = (activity) => {
    switch (activity.type) {
      case 'user_registered': return `انضمام طالب جديد: ${activity.details}`;
      case 'course_created': return `تم نشر كورس: ${activity.details}`;
      case 'subscription_created': return `اشتراك مالي جديد: ${activity.details}`;
      case 'teacher_approved': return `تم اعتماد المدرس: ${activity.details}`;
      case 'teacher_rejected': return `رفض طلب المدرس: ${activity.details}`;
      default: return activity.details;
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 min-h-[500px]">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse uppercase tracking-widest text-xs">جاري تهيئة مركز الإدارة...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-10 px-4 sm:px-6 py-6 min-h-screen bg-gray-50/30">
      
      {/* Executive Header Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-primary-900 rounded-[2.5rem] p-8 lg:p-12 text-white shadow-2xl">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary-600/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-600/5 rounded-full blur-3xl"></div>
        
        <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="text-center md:text-right">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.2em] mb-4">
                <ShieldCheck size={14} className="text-primary-400" /> لوحة التحكم الإدارية العليا
            </div>
            <h1 className="text-3xl lg:text-5xl font-black mb-3 tracking-tight leading-tight">مركز قيادة المنصة</h1>
            <p className="text-gray-400 text-lg font-medium opacity-90 max-w-xl leading-relaxed">
              مرحباً بك في لوحة تحكم الإدارة. يمكنك متابعة النمو، إدارة المستخدمين، وضمان جودة المحتوى التعليمي.
            </p>
          </div>

          <div className="flex flex-col items-center gap-4">
             <div className="bg-white/5 backdrop-blur-xl border border-white/10 px-8 py-4 rounded-[2rem] text-center shadow-inner group hover:bg-white/10 transition-all">
                <p className="text-[10px] font-black uppercase tracking-widest text-primary-400 mb-1">تاريخ اليوم</p>
                <p className="font-black text-xl">{getArabicDate(new Date())}</p>
             </div>
             <button className="flex items-center gap-2 text-xs font-bold text-gray-400 hover:text-white transition-colors">
                <Clock size={14} /> آخر تحديث: الآن
             </button>
          </div>
        </div>
      </div>

      {/* Main Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { title: 'إجمالي المستخدمين', value: stats?.totalUsers || 0, icon: Users, color: 'from-blue-600 to-blue-700', shadow: 'shadow-blue-100' },
          { title: 'إجمالي الكورسات', value: stats?.totalCourses || 0, icon: BookOpen, color: 'from-emerald-600 to-emerald-700', shadow: 'shadow-emerald-100' },
          { title: 'إجمالي الاشتراكات', value: stats?.activeSubscriptions || stats?.totalSubscriptions || 0, icon: CreditCard, color: 'from-violet-600 to-violet-700', shadow: 'shadow-violet-100' },
          { title: 'الإيرادات الشهرية', value: `${stats?.monthlyRevenue || 0} ج.م`, icon: DollarSign, color: 'from-amber-500 to-orange-600', shadow: 'shadow-orange-100' }
        ].map((card, index) => (
          <div key={index} className={`relative overflow-hidden bg-gradient-to-br ${card.color} rounded-[2rem] p-6 text-white shadow-xl ${card.shadow} transition-transform hover:-translate-y-1 duration-300`}>
            <div className="absolute top-0 left-0 w-24 h-24 bg-white/10 rounded-br-full -z-0"></div>
            <div className="relative z-10">
              <div className="bg-white/20 w-12 h-12 rounded-xl flex items-center justify-center mb-6 backdrop-blur-md">
                <card.icon size={24} />
              </div>
              <p className="text-white/70 text-[10px] font-black uppercase tracking-widest mb-1">{card.title}</p>
              <p className="text-3xl font-black tracking-tighter">{card.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Access Management Hub */}
      <div className="space-y-6">
        <div className="flex items-center gap-2 px-2">
            <PieChart size={22} className="text-primary-600" />
            <h2 className="text-2xl font-black text-gray-900">الوصول السريع للأقسام</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {navigationItems.map((item, index) => (
                <button
                    key={index}
                    onClick={() => navigate(item.link)}
                    className="bg-white rounded-[2.2rem] shadow-sm hover:shadow-2xl transition-all duration-300 p-8 text-right group border border-gray-100 hover:border-primary-500 relative overflow-hidden"
                >
                    <div className="absolute -top-10 -left-10 w-32 h-32 bg-gray-50 rounded-full group-hover:bg-primary-50 transition-colors"></div>
                    <div className={`${item.color} relative z-10 w-14 h-14 rounded-2xl flex items-center justify-center mb-6 shadow-xl ${item.shadow} group-hover:scale-110 transition-transform duration-300`}>
                        <item.icon size={28} className="text-white" />
                    </div>
                    <div className="relative z-10">
                        <h3 className="text-lg font-black text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                            {item.title}
                        </h3>
                        <p className="text-xs text-gray-500 font-medium leading-relaxed mb-6 h-8 line-clamp-2">
                            {item.description}
                        </p>
                        <div className="flex items-center text-primary-600 text-[10px] font-black uppercase tracking-tighter">
                            إدارة القسم <ChevronLeft size={16} className="mr-1 group-hover:translate-x-[-4px] transition-transform" />
                        </div>
                    </div>
                </button>
            ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Recent Activities Section (2/3 Width) */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden group hover:shadow-xl transition-all duration-500">
            <div className="p-8 border-b border-gray-50 flex items-center justify-between bg-gray-50/30">
                <h2 className="text-xl font-black text-gray-900 flex items-center gap-3">
                    <Activity className="text-primary-600" />
                    سجل الأنشطة والعمليات
                </h2>
                <span className="text-[10px] font-black text-gray-400 bg-white px-4 py-1.5 rounded-full border border-gray-100 uppercase tracking-widest">مباشر الآن</span>
            </div>
            
            <div className="p-4 sm:p-8 space-y-4">
              {activities?.activities?.slice(0, 5).map((activity, index) => (
                <div key={index} className="flex items-center gap-5 p-5 bg-gray-50/50 rounded-3xl border border-gray-50 group/item hover:bg-white hover:shadow-lg hover:border-primary-100 transition-all duration-300">
                  <div className="bg-white w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm flex-shrink-0 group-hover/item:scale-110 transition-transform">
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-bold text-gray-800 leading-snug truncate">
                        {getActivityText(activity)}
                    </p>
                    <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-[10px] font-black text-primary-600 bg-primary-50 px-2.5 py-0.5 rounded-lg uppercase tracking-tighter">{activity.type.replace('_', ' ')}</span>
                        <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
                        <span className="text-[10px] font-bold text-gray-400 flex items-center gap-1">
                            <Clock size={10} /> {getArabicDate(activity.created_at)}
                        </span>
                    </div>
                  </div>
                  <button className="p-2 text-gray-300 hover:text-primary-600 transition-colors">
                      <ChevronLeft size={20} />
                  </button>
                </div>
              ))}
              
              {(!activities?.activities || activities.activities.length === 0) && (
                <div className="py-20 text-center">
                  <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertCircle size={40} className="text-gray-200" />
                  </div>
                  <p className="text-gray-500 font-bold">لا توجد عمليات مسجلة في السجل حالياً.</p>
                </div>
              )}
            </div>

            <div className="p-6 bg-gray-50/50 border-t border-gray-50 text-center">
                <button className="text-sm font-black text-gray-400 hover:text-primary-600 transition-colors uppercase tracking-widest flex items-center justify-center gap-2 mx-auto">
                    عرض كافة العمليات السابقة <ArrowRight size={14} className="rotate-180" />
                </button>
            </div>
          </div>
        </div>

        {/* Quick Insights Sidebar (1/3 Width) */}
        <div className="space-y-8">
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 space-y-8">
            <h2 className="text-xl font-black text-gray-900 flex items-center gap-3">
              <Sparkles className="text-primary-600" size={20} />
              تحليلات النمو السريع
            </h2>

            <div className="space-y-4">
                {[
                    { label: 'مستخدمون جدد (شهرياً)', value: stats?.newUsersThisMonth || 0, icon: UserPlus, color: 'text-blue-600', bg: 'bg-blue-50' },
                    { label: 'كورسات منشورة حديثاً', value: stats?.newCoursesThisMonth || 0, icon: Layers, color: 'text-emerald-600', bg: 'bg-emerald-50' },
                    { label: 'مدرسون في الانتظار', value: stats?.pendingTeachers || 0, icon: Clock, color: 'text-rose-600', bg: 'bg-rose-50', urgent: true },
                    { label: 'معدل النمو السنوي', value: `${stats?.growthRate || 0}%`, icon: TrendingUp, color: 'text-amber-500', bg: 'bg-amber-50' }
                ].map((insight, i) => (
                    <div key={i} className={`p-5 rounded-3xl flex items-center justify-between group transition-all hover:bg-gray-50 ${insight.urgent && insight.value > 0 ? 'ring-2 ring-rose-100' : 'border border-gray-50'}`}>
                        <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-xl ${insight.bg} ${insight.color} group-hover:scale-110 transition-transform`}>
                                <insight.icon size={18} />
                            </div>
                            <span className="text-sm font-bold text-gray-600">{insight.label}</span>
                        </div>
                        <span className={`text-base font-black ${insight.urgent && insight.value > 0 ? 'text-rose-600' : 'text-gray-900'}`}>{insight.value}</span>
                    </div>
                ))}
            </div>
          </div>

          {/* Motivational Admin Card */}
          <div className="bg-gray-900 rounded-[2.5rem] p-10 text-white relative overflow-hidden shadow-2xl group">
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary-600/20 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>
            <div className="relative z-10 space-y-6">
                <div className="bg-primary-600 w-12 h-12 rounded-2xl flex items-center justify-center shadow-xl">
                    <BarChart3 size={24} />
                </div>
                <h3 className="text-2xl font-black leading-tight">الشفافية في البيانات <br /> هي سر النجاح.</h3>
                <p className="text-gray-400 text-sm font-medium leading-relaxed">
                    استخدم التقارير التفصيلية لفهم سلوك الطلاب وتحسين أداء المدرسين لرفع جودة المنصة التعليمية.
                </p>
                <div className="pt-4">
                    <button className="w-full bg-white text-gray-900 font-black py-4 rounded-2xl hover:bg-primary-50 transition-all flex items-center justify-center gap-2 transform active:scale-95 shadow-xl">
                        تصدير التقارير السنوية <ArrowRight size={18} className="rotate-180" />
                    </button>
                </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AdminDashboard;